import { useMemo } from 'react';

import { useGetIssuesForProperty } from './useGetIssuesForProperty';

import { Issue, urgencyOrder } from '@/models/Issue';

/**
 * Shared by ConditionScoreView (Top Concerns / condition scoring) and
 * MarketInsightsView (negotiation leverage callout) so the two surfaces
 * can't silently quote different repair-cost totals for the same property.
 */
export interface IssueItem {
  title: string;
  urgency: string;
  cost: string;
  costMid: number;
  costMin: number;
  costMax: number;
  category: string;
  trade?: string;
  componentCategory?: string;
  reportType?: string;
  description?: string;
}

export interface TopConcernGroup {
  key: string;
  issues: IssueItem[];
  critCount: number;
  urgCount: number;
  modCount: number;
  totalMin: number;
  totalMax: number;
  totalCostMid: number;
  score: number;
}

const formatCostRange = (issue: Issue): string => {
  if (issue.costEstimateMin == null && issue.costEstimateMax == null) return 'N/A';
  const sym = issue.currencySymbol ?? '$';
  if (issue.costEstimateMin != null && issue.costEstimateMax != null) {
    return `${sym}${issue.costEstimateMin.toLocaleString()} – ${sym}${issue.costEstimateMax.toLocaleString()}`;
  }
  const val = issue.costEstimateMin ?? issue.costEstimateMax ?? 0;
  return `${sym}${val.toLocaleString()}`;
};

// Trade-focused specialists (ROOF, HVAC, etc.) own their entire trade — a GHI
// finding with the same trade is suppressed regardless of componentCategory.
// Broad specialists (PEST) can appear across many trades, so those only
// suppress GHI at the exact trade+component level.
const TRADE_FOCUSED = new Set([
  'ROOF',
  'POOL',
  'CHIMNEY',
  'HVAC',
  'ELEC',
  'PLUMB',
  'FOUND',
  'SEWER',
  'SHI',
]);

// Specialist-report dedup: suppresses a general-inspection (GHI) finding when
// a specialist report already covers the same system, so the same underlying
// defect isn't counted twice toward cost/leverage totals.
export const mapIssuesToItems = (issues: Issue[] | undefined): IssueItem[] => {
  if (!issues?.length) return [];

  const mapped = [...issues]
    .sort((a, b) => (urgencyOrder[a.urgency] ?? 99) - (urgencyOrder[b.urgency] ?? 99))
    .map((issue) => ({
      title: issue.title,
      urgency: issue.urgency,
      cost: formatCostRange(issue),
      costMin: issue.costEstimateMin ?? 0,
      costMax: issue.costEstimateMax ?? issue.costEstimateMin ?? 0,
      costMid:
        ((issue.costEstimateMin ?? 0) + (issue.costEstimateMax ?? issue.costEstimateMin ?? 0)) / 2,
      category: issue.category,
      trade: issue.trade,
      componentCategory: issue.componentCategory,
      reportType: issue.reportType,
      description: issue.description,
    }));

  const tradeKeys = new Set<string>(); // suppress any GHI issue with this trade
  const componentKeys = new Set<string>(); // suppress GHI issue with exact trade+component
  for (const item of mapped) {
    if (item.reportType && item.reportType !== 'GHI') {
      if (TRADE_FOCUSED.has(item.reportType)) {
        tradeKeys.add(item.trade ?? '');
      } else {
        componentKeys.add(`${item.trade ?? ''}|${item.componentCategory ?? item.category}`);
      }
    }
  }
  if (tradeKeys.size === 0 && componentKeys.size === 0) return mapped;
  return mapped.filter((item) => {
    if (item.reportType !== 'GHI') return true;
    if (tradeKeys.has(item.trade ?? '')) return false;
    const key = `${item.trade ?? ''}|${item.componentCategory ?? item.category}`;
    return !componentKeys.has(key);
  });
};

/**
 * Groups issues by trade (falling back to category), ranks groups by urgency
 * severity + cost size, and returns the top N as negotiation leverage — the
 * same "Repair Credits (Top Concerns)" figure shown in NegotiationStrategyDialog.
 */
export const computeTopConcernGroups = (
  issueList: IssueItem[],
  limit = 4,
): { topGroups: TopConcernGroup[]; leverageMin: number; leverageMax: number } => {
  const tradeMap = new Map<string, Omit<TopConcernGroup, 'key' | 'score'>>();
  for (const issue of issueList) {
    const key = issue.trade ?? issue.category;
    if (!tradeMap.has(key)) {
      tradeMap.set(key, {
        issues: [],
        critCount: 0,
        urgCount: 0,
        modCount: 0,
        totalMin: 0,
        totalMax: 0,
        totalCostMid: 0,
      });
    }
    const g = tradeMap.get(key);
    if (!g) continue;
    g.issues.push(issue);
    if (issue.urgency === 'Critical') g.critCount++;
    else if (issue.urgency === 'Urgent') g.urgCount++;
    else if (issue.urgency === 'Moderate') g.modCount++;
    g.totalMin += issue.costMin;
    g.totalMax += issue.costMax;
    g.totalCostMid += issue.costMid;
  }

  const topGroups = [...tradeMap.entries()]
    .map(([key, g]) => ({
      key,
      ...g,
      score: g.critCount * 4 + g.urgCount * 3 + g.modCount * 2 + g.totalCostMid / 1000,
    }))
    .sort((a, b) => b.score - a.score)
    .slice(0, limit);

  const leverageMin = topGroups.reduce((s, g) => s + g.totalMin, 0);
  const leverageMax = topGroups.reduce((s, g) => s + g.totalMax, 0);

  return { topGroups, leverageMin, leverageMax };
};

export interface UseNegotiationLeverageResult {
  issueList: IssueItem[];
  topGroups: TopConcernGroup[];
  leverageMin: number;
  leverageMax: number;
  isLoading: boolean;
}

export function useNegotiationLeverage(
  propertyId: string | undefined,
): UseNegotiationLeverageResult {
  const { issues, isInitialFetching } = useGetIssuesForProperty(propertyId ?? '');
  const issueList = useMemo(() => mapIssuesToItems(issues), [issues]);
  const { topGroups, leverageMin, leverageMax } = useMemo(
    () => computeTopConcernGroups(issueList),
    [issueList],
  );

  return { issueList, topGroups, leverageMin, leverageMax, isLoading: isInitialFetching };
}

// ─── Commission comparison ──────────────────────────────────────────────
// Same rate/formula as NegotiationStrategyDialog's "Hauser Fee vs.
// Traditional Commission" line, kept here so the two surfaces can't quote
// different numbers for the same basis price.
export const TRADITIONAL_COMMISSION_RATE = 0.025;
export const DEFAULT_HAUSER_FEE = 20000;

export const computeCommissionSavings = (
  basisPrice: number,
  hauserFee: number = DEFAULT_HAUSER_FEE,
): { traditionalCommission: number; netSavings: number } => {
  const traditionalCommission = Math.round(basisPrice * TRADITIONAL_COMMISSION_RATE);
  const netSavings = Math.max(0, traditionalCommission - hauserFee);
  return { traditionalCommission, netSavings };
};
